import { useState, useCallback, useEffect } from "react";
import {
  User as FirebaseUser,
  onAuthStateChanged,
  getIdToken,
} from "firebase/auth";
import { auth } from "../firebase/config";

interface User extends FirebaseUser {
  accessToken?: string;
  role?: string;
}

interface TokenData {
  token: string;
  expiresAt: number;
}

interface UseFirebaseAuthReturn {
  loading: boolean;
  user: User | null;
}



export const useFirebaseAuth = (): UseFirebaseAuthReturn => {
  const [loading, setLoading] = useState(true);
  const [user, setUser] = useState<User | null>(null);

  const storeToken = useCallback((token: string) => {
    try {
      const decodedToken = JSON.parse(atob(token.split(".")[1]));
      const expiresAt = decodedToken.exp * 1000; // Convert to milliseconds
      const tokenData: TokenData = {
        token,
        expiresAt,
      };
      sessionStorage.setItem("firebase_token", JSON.stringify(tokenData));
    } catch (error) {
      console.error("Error storing token:", error);
    }
  }, []);

  const getStoredToken = useCallback((): TokenData | null => {
    try {
      const storedData = sessionStorage.getItem("firebase_token");
      return storedData ? JSON.parse(storedData) : null;
    } catch (error) {
      console.error("Error reading stored token:", error);
      return null;
    }
  }, []);

  const checkAndRefreshToken = useCallback(
    async (currentUser: User) => {
      try {
        const storedTokenData = getStoredToken();
        const currentTime = Date.now();

        // If we have a stored token and it's not expired (with 5 min buffer)
        if (
          storedTokenData &&
          storedTokenData.expiresAt > currentTime + 5 * 60 * 1000
        ) {
          return storedTokenData.token;
        }

        // Token is expired or about to expire, get a new one
        const newToken = await getIdToken(currentUser, true);
        storeToken(newToken);
        return newToken;
      } catch (error) {
        console.error("Error refreshing token:", error);
        throw error;
      }
    },
    [getStoredToken, storeToken]
  );




  // Add auth state listener
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(
      auth,
      async (currentUser: User | null) => {
        if (currentUser) {
          try {
            const token = await checkAndRefreshToken(currentUser);
            currentUser.accessToken = token;
          } catch (error) {
            console.error("Error in auth state change:", error);
          }
        }
        setUser(currentUser);
        setLoading(false);
      }
    );

    return () => unsubscribe();
  }, [checkAndRefreshToken]);

  return {
    loading,
    user,
  };
};
